import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import PageLayout from '../components/PageLayout';
import Pagination from '../components/Pagination';
import RouteMap from '../components/RouteMap';
import AdBanner from '../components/AdBanner';
import NativeAdCard from '../components/NativeAdCard';
import QuizCTABanner from '../components/QuizCTABanner';
import { useAds, injectAds } from '../hooks/useAds';
import axios from 'axios';
import { Compass, TrendingUp, MapPin, Search, Plane, Globe, Filter, Map } from 'lucide-react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';

import { API } from '../config';

const CROWD_BADGE = {
  low: 'bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30',
  medium: 'bg-[#FF4500]/15 text-[#FF4500] border-[#FF4500]/30',
  high: 'bg-red-100 text-red-600 border-red-200 dark:bg-red-900/20 dark:text-red-400',
};

const REGIONS = [
  { value: 'all', label: 'All Regions' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'AE', label: 'UAE' },
  { value: 'QA', label: 'Qatar' },
  { value: 'KW', label: 'Kuwait' },
  { value: 'BH', label: 'Bahrain' },
  { value: 'OM', label: 'Oman' },
  { value: 'JO', label: 'Jordan' },
  { value: 'TR', label: 'Turkey' },
  { value: 'EG', label: 'Egypt' },
  { value: 'GB', label: 'UK' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'ES', label: 'Spain' },
  { value: 'IT', label: 'Italy' },
  { value: 'SG', label: 'Singapore' },
  { value: 'JP', label: 'Japan' },
  { value: 'KR', label: 'South Korea' },
  { value: 'IN', label: 'India' },
  { value: 'US', label: 'USA' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'BR', label: 'Brazil' },
];

export default function Discover() {
  const navigate = useNavigate();
  const [airports, setAirports] = useState([]);
  const [trendingFlights, setTrendingFlights] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [topRoutes, setTopRoutes] = useState([]);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [country, setCountry] = useState('all');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const LIMIT = 24;
  const ads = useAds('discover');

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [country]);

  const fetchAirports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (country && country !== 'all') params.append('country', country);
      const res = await axios.get(`${API}/airports/paginated?${params}`);
      setAirports(res.data.airports);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, country]);

  useEffect(() => { fetchAirports(); }, [fetchAirports]);

  // Fetch sidebar data once
  useEffect(() => {
    axios.get(`${API}/discover`).then(r => {
      setTrendingFlights(r.data.trending_flights || []);
      setRecentPosts(r.data.recent_posts || []);
    }).catch(() => {});
    // Fetch top routes for map
    axios.get(`${API}/map/routes?limit=80`).then(r => {
      setTopRoutes(r.data || []);
    }).catch(() => {});
  }, []);

  return (
    <PageLayout>
      <SEOHead
        title="Discover 164+ Airports Worldwide"
        description="Explore 164 airports worldwide with real-time feeds, crowd levels, and community posts. Find trending flights and connect with travelers at any airport."
        url="/discover"
        keywords="discover airports, airport directory, 164 airports, trending flights, airport community"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Compass className="w-6 h-6 text-[#FF4500]" />
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Discover Airports</h1>
          </div>
          <p className="text-muted-foreground">{total > 0 ? `${total} airports worldwide` : 'Explore airports worldwide'}</p>
        </div>

        {/* ── Top Routes Map ─────────────────────────────────────── */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Map className="w-5 h-5 text-[#FF4500]" />
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
                Live Route Map
              </h2>
              <Badge className="text-[10px] bg-[#FF4500]/15 text-[#FF4500] border-[#FF4500]/30">
                {topRoutes.length} routes
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground h-7"
              onClick={() => setMapExpanded(p => !p)}
            >
              {mapExpanded ? 'Collapse' : 'Expand'}
            </Button>
          </div>
          {topRoutes.length > 0 ? (
            <RouteMap
              routes={topRoutes}
              height={mapExpanded ? '640px' : '420px'}
              className="transition-all duration-500"
            />
          ) : (
            <Skeleton className="rounded-2xl w-full" style={{ height: '420px' }} />
          )}
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Click any route line or airport dot to explore the community · Animated lines show most active routes
          </p>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, city, or IATA code..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="airport-search-input"
            />
          </div>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="w-48" data-testid="country-filter">
              <Filter className="w-3.5 h-3.5 mr-1.5" />
              <SelectValue placeholder="Filter by country" />
            </SelectTrigger>
            <SelectContent>
              {REGIONS.map(r => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(search || country !== 'all') && (
            <Button variant="outline" onClick={() => { setSearch(''); setCountry('all'); }}>Clear</Button>
          )}
        </div>

        <div className="grid lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {/* Airport Grid */}
          <div className="lg:col-span-2 xl:col-span-3">
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[...Array(12)].map((_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}
              </div>
            ) : airports.length === 0 ? (
              <div className="text-center py-16">
                <Globe className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No airports found</p>
                <Button variant="outline" onClick={() => { setSearch(''); setCountry(''); }} className="mt-3">Clear filters</Button>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                  {injectAds(airports, ads, 3).map((apt, i) =>
                    apt.__isAd
                      ? <NativeAdCard key={`ad-${apt.ad_id}-${i}`} ad={apt} variant="discover" />
                      : (
                    <motion.div key={apt.iata_code} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                      <Link to={`/airport/${apt.iata_code}`} data-testid="airport-card">
                        <Card className="fly-card overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer h-full">
                          <div className="h-36 overflow-hidden relative">
                            <img
                              src={apt.image_url}
                              alt={apt.city}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={e => { e.target.src = 'https://images.unsplash.com/photo-1771694583789-71b695b24ad6?w=400&q=80'; }}
                            />
                            <div className="absolute bottom-2 left-2">
                              <span className="text-white text-2xl font-bold drop-shadow-lg" style={{ fontFamily: 'Space Grotesk' }}>{apt.iata_code}</span>
                            </div>
                            {apt.flag && (
                              <span className="absolute top-2 right-2 text-xl drop-shadow">{apt.flag}</span>
                            )}
                          </div>
                          <div className="p-3">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-sm font-semibold truncate" title={apt.name}>{apt.city}</p>
                              <Badge className={`text-[10px] capitalize flex-shrink-0 ml-1 ${CROWD_BADGE[apt.crowd_level] || CROWD_BADGE.medium}`}>
                                {apt.crowd_level}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{apt.country}</p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                              <span>{apt.post_count || 0} posts</span>
                              <span>{apt.terminals?.length || 0} terminals</span>
                              {apt.wifi && <span className="text-[#10B981]">WiFi</span>}
                            </div>
                          </div>
                        </Card>
                      </Link>
                    </motion.div>
                      )
                  )}
                </div>

                <Pagination
                  page={page}
                  pages={pages}
                  total={total}
                  limit={LIMIT}
                  onPageChange={p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  data-testid="airports-pagination"
                />
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                <TrendingUp className="w-4 h-4 text-[#FF4500]" /> Trending Flights
              </h2>
              <div className="space-y-2">
                {trendingFlights.slice(0, 6).map(f => (
                  <Card key={f.flight_id} className="fly-card p-3 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/route/${f.route_id || `${f.origin_code}-${f.destination_code}`}`)}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-bold" style={{ fontFamily: 'Space Grotesk' }}>{f.flight_number}</div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <span>{f.origin_code}</span><Plane className="w-2.5 h-2.5" /><span>{f.destination_code}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={`text-[10px] ${ f.status === 'on_time' ? 'status-on-time' : f.status === 'boarding' ? 'status-boarding' : 'status-delayed' }`}>
                          {f.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Live Feed</h2>
              <div className="space-y-2">
                {recentPosts.slice(0, 4).map(post => (
                  <Card key={post.post_id} className="fly-card p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <MapPin className="w-3 h-3 text-[#FF4500]" />
                      <span className="text-xs font-medium">{post.airport_code}</span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">{post.content}</p>
                  </Card>
                ))}
              </div>
            </div>

            {/* Ad space — native card if available, fallback to placeholder */}
            {ads.length > 0
              ? <NativeAdCard ad={ads[0]} variant="sidebar" />
              : <AdBanner variant="sidebar" />
            }

            {/* Quiz CTA */}
            <QuizCTABanner variant="sidebar" />
          </div>{/* end sidebar */}
        </div>{/* end grid */}
      </div>
    </PageLayout>
  );
}
